import identifyOptimoveUser from "@casino/client/optimove/identifyUser";
import setDataToGib from "@casino/legacy/client/components/Auth/Shared/utils/setDataToGib";
import useUpdateUserLanguage from "@casino/legacy/client/features/SocialAuth/hooks/useUpdateUserLanguage";
import useFeature from "@casino/legacy/client/hooks/feature/useFeature";
import useLanguage from "@casino/legacy/client/hooks/useLanguage";
import useSite from "@casino/legacy/client/hooks/useSite";
import startFraudEngine from "@casino/legacy/client/shared/fraud/fraudEngine";
import COUNTRY_CODE from "@workspace/casino_config/cookies/COUNTRY_CODE";
import type { RegisterMutation } from "@workspace/casino_core/mutations/RegisterMutation";
import { useUpdateMyDefaultCurrencyMutation } from "@workspace/casino_core/mutations/UpdateMyDefaultCurrencyMutation.client";
import { useUpdateUserSettingsMutation } from "@workspace/casino_core/mutations/UpdateUserSettingsMutation.client";
import { getCookie, setCookie } from "@workspace/cookies/browser";
import { type RegistrationProperties, track } from "@workspace/tracking";
import { useState } from "react";
import useHandleSportsbetRegister from "../useHandleSportsbetRegister";
import useRequestEmailVerification from "../useRequestEmailVerification";

const useHandleRegisterResponse = () => {
  const [loading, setLoading] = useState(false);
  const [updateUserSettings] = useUpdateUserSettingsMutation();
  const isMostPopularCryptosEnabled = useFeature("isMostPopularCryptosEnabled");
  const { requestEmailVerification } = useRequestEmailVerification();
  const { isBridgeSite, site, siteConfig } = useSite();
  const updateUserLanguage = useUpdateUserLanguage();
  const { code: language } = useLanguage();
  const { handleSportsbetRegister } = useHandleSportsbetRegister();
  const [updateMyCurrency] = useUpdateMyDefaultCurrencyMutation();
  const countryCode = getCookie(COUNTRY_CODE);

  const handleRegisterResponse = async (
    data: RegisterMutation["userManagementRegisterAsUser"],
    trackingProperties: RegistrationProperties,
  ) => {
    const {
      errors,
      token = "",
      deviceDataToken = "",
      userManagement,
      sessionId,
    } = data ?? {};
    if (errors) {
      setLoading(false);
      return { errors };
    }

    setCookie("token", token);
    setDataToGib(userManagement?.currentProfile?._id || "", sessionId || "");
    if (isMostPopularCryptosEnabled) {
      await updateUserSettings({
        value: "BTC,ETH,TRX,USDT",
        site,
        name: "wallets",
      });
    }
    startFraudEngine({ deviceDataToken });
    track("Registration", {
      userId: userManagement?.currentProfile?._id,
      ...trackingProperties,
    });
    const optimoveUserId = userManagement?.currentProfile?._id;
    if (optimoveUserId) {
      identifyOptimoveUser(optimoveUserId);
    }
    await requestEmailVerification();

    if (language !== siteConfig.defaultLang) {
      await updateUserLanguage(language);
    }

    if (isBridgeSite) {
      await handleSportsbetRegister();
    }
    if (countryCode === "TR") {
      await updateMyCurrency({ currency: "TRY" });
    }
    return { userManagement };
  };

  return {
    loading,
    handleRegisterResponse,
  };
};

export default useHandleRegisterResponse;
